import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { getSessionFromCookie } from "@/hooks/useAuthPage";

const useSession = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  async function getSession() {
    try {
      const sessionFromCookie = getSessionFromCookie();
      
      // Only try to set session if we have valid tokens
      if (sessionFromCookie.access_token && sessionFromCookie.refresh_token) {
        const { data, error } = await supabase.auth.setSession({
          access_token: sessionFromCookie.access_token,
          refresh_token: sessionFromCookie.refresh_token,
        });
        if (error) {
          setError(error);
          console.error("Error getting session:", error);
        } else {
          setSession(data.session);
        }
      } else {
        // No valid session in cookies, try to get current session from Supabase
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          setError(error);
          console.error("Error getting current session:", error);
        } else {
          setSession(data.session);
        }
      }
    } catch (error) {
      setError(error);
      console.error("Error getting session:", error);
    }
  }
  async function getUser() {
    try {
      const sessionFromCookie = getSessionFromCookie();
      
      // Only try to set session if we have valid tokens
      if (sessionFromCookie.access_token && sessionFromCookie.refresh_token) {
        const { data, error } = await supabase.auth.setSession({
          access_token: sessionFromCookie.access_token,
          refresh_token: sessionFromCookie.refresh_token,
        });
        if (error) {
          setError(error);
          console.error("Error getting user:", error);
        } else {
          setUser(data.session?.user || null);
        }
      } else {
        // No valid session in cookies, try to get current user from Supabase
        const { data, error } = await supabase.auth.getUser();
        if (error) {
          setError(error);
          console.error("Error getting current user:", error);
        } else {
          setUser(data.user);
        }
      }
    } catch (error) {
      setError(error);
      console.error("Error getting user:", error);
    }
  }

  const refresh = async () => {
    setLoading(true);
    try {
      await Promise.all([getSession(), getUser()]);
    } catch (error) {
      setError(error);
      console.error("Error refreshing session:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if we already have a valid session to avoid unnecessary loading
    const checkExistingSession = async () => {
      try {
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        if (existingSession?.user) {
          // We already have a valid session, no need to load
          setSession(existingSession);
          setUser(existingSession.user);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error("Error checking existing session:", error);
      }
      
      // No existing session, do full refresh
      refresh();
    };

    checkExistingSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        // We can call refresh here if there are other things to update based on session
        // For now, directly setting session and user is sufficient
        if (
          event === "SIGNED_IN" ||
          event === "SIGNED_OUT" ||
          event === "USER_UPDATED"
        ) {
          // You might want to set loading to true here if refresh does more async work
          // setLoading(true);
          // await refresh(); // Or just getSession() and getUser() if refresh does too much
        }
        // Ensure loading is false after initial auth event or if not doing a full refresh
        if (loading) {
          setLoading(false);
        }
      }
    );

    return () => {
      if (authListener?.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  return { session, error, user, loading, refresh };
};

export default useSession;
